import { useState, useCallback } from 'react';
import { createInsuranceCompany } from '../api/insurance';
import type { CreateInsuranceCompanyRequest, InsuranceCompanyDto } from '../types/insurance';

interface UseCreateInsuranceCompanyResult {
  create: (data: CreateInsuranceCompanyRequest) => Promise<InsuranceCompanyDto>;
  isLoading: boolean;
  error: string | null;
  reset: () => void;
}

export function useCreateInsuranceCompany(
  onSuccess?: (company: InsuranceCompanyDto) => void
): UseCreateInsuranceCompanyResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(
    async (data: CreateInsuranceCompanyRequest): Promise<InsuranceCompanyDto> => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await createInsuranceCompany(data);
        onSuccess?.(result);
        return result;
      } catch (err) {
        const apiError = err as { response?: { data?: { message?: string } } };
        const message = apiError.response?.data?.message || 'Failed to create insurance company';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccess]
  );

  const reset = useCallback(() => {
    setError(null);
  }, []);

  return { create, isLoading, error, reset };
}
